import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  MenuItem,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  Alert,
  Tooltip,
  CircularProgress,
  Snackbar,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
import {
  Search,
  Refresh,
  Edit,
  Delete,
  Close,
  Save,
  CloudUpload,
  Image as ImageIcon,
  Check,
  Remove,
  Description,
  Download,
  Upload,
  FileUpload,
} from '@mui/icons-material';
import {
  productInfoService,
  ProductInfoListItem,
  ProductAdditionalInfo,
  ProductImage,
  CsvValidationResponse,
  CsvImportResponse,
  CsvImportRowResult,
} from '../../services/productInfoService';
import { API_BASE_URL } from '../../config/constants';
import { getBackofficeAuthHeaders } from '../../services/api';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';

// Quill toolbar configuration
const QUILL_MODULES = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ color: [] }, { background: [] }],
    ['link'],
    ['clean'],
  ],
};

const QUILL_FORMATS = [
  'header',
  'bold', 'italic', 'underline', 'strike',
  'list',
  'color', 'background',
  'link',
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type InfoFilter = 'all' | 'with_info' | 'without_info';
type FinishType = 'Matte' | 'Glossy' | 'Semi-Glossy' | 'Satin';

interface SnackState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'info' | 'warning';
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const FINISH_TYPES: FinishType[] = ['Matte', 'Glossy', 'Semi-Glossy', 'Satin'];

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`product-info-tabpanel-${index}`}
    aria-labelledby={`product-info-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
  </div>
);

// ---------------------------------------------------------------------------
// CSV Import Dialog Component
// ---------------------------------------------------------------------------

interface CsvImportDialogProps {
  open: boolean;
  onClose: () => void;
}

const CsvImportDialog: React.FC<CsvImportDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<'upload' | 'validating' | 'validation' | 'importing' | 'done'>('upload');
  const [file, setFile] = useState<File | null>(null);
  const [validation, setValidation] = useState<CsvValidationResponse | null>(null);
  const [importResult, setImportResult] = useState<CsvImportResponse | null>(null);
  const [error, setError] = useState('');

  const reset = () => {
    setStep('upload');
    setFile(null);
    setValidation(null);
    setImportResult(null);
    setError('');
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFileSelect = async (selectedFile: File) => {
    setFile(selectedFile);
    setError('');
    setStep('validating');

    try {
      const result = await productInfoService.validateCsv(selectedFile);
      setValidation(result);
      setStep('validation');
    } catch (e: any) {
      setError(e.message || 'Validation failed');
      setStep('upload');
    }
  };

  const handleImport = async () => {
    if (!file) return;
    setStep('importing');
    setError('');

    try {
      const result = await productInfoService.importCsv(file);
      setImportResult(result);
      setStep('done');
    } catch (e: any) {
      setError(e.message || 'Import failed');
      setStep('validation');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'success': return 'success' as const;
      case 'partial': return 'warning' as const;
      case 'error': return 'error' as const;
      default: return 'default' as const;
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FileUpload />
          Import Product Information
        </Box>
        <IconButton onClick={handleClose} size="small"><Close /></IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        {/* Step: Upload */}
        {step === 'upload' && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload a CSV file with product information. Use the "Template" button to download the standard format.
            </Typography>
            <Box
              sx={{
                border: '2px dashed',
                borderColor: 'divider',
                borderRadius: 2,
                p: 4,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
              }}
              onClick={() => document.getElementById('csv-upload-input')?.click()}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                const f = e.dataTransfer.files[0];
                if (f && f.name.endsWith('.csv')) handleFileSelect(f);
              }}
            >
              <CloudUpload sx={{ fontSize: 48, color: 'action.active', mb: 1 }} />
              <Typography>Click or drag CSV file here</Typography>
              <Typography variant="caption" color="text.secondary">
                Only .csv files accepted
              </Typography>
              <input
                id="csv-upload-input"
                type="file"
                accept=".csv"
                hidden
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) handleFileSelect(f);
                  e.target.value = '';
                }}
              />
            </Box>
          </Box>
        )}

        {/* Step: Validating */}
        {step === 'validating' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography>Validating {file?.name}...</Typography>
          </Box>
        )}

        {/* Step: Validation Results */}
        {step === 'validation' && validation && (
          <Box>
            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
              <Paper sx={{ p: 2, flex: 1, textAlign: 'center' }}>
                <Typography variant="h4">{validation.total_rows}</Typography>
                <Typography variant="caption">Total Rows</Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                <Typography variant="h4">{validation.valid_rows}</Typography>
                <Typography variant="caption">Valid</Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1, textAlign: 'center', bgcolor: validation.error_rows > 0 ? 'error.light' : 'grey.100', color: validation.error_rows > 0 ? 'error.contrastText' : 'text.primary' }}>
                <Typography variant="h4">{validation.error_rows}</Typography>
                <Typography variant="caption">Errors</Typography>
              </Paper>
            </Box>

            {validation.errors.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Validation Errors:</Typography>
                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 300 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell>Row</TableCell>
                        <TableCell>Part Number</TableCell>
                        <TableCell>Errors</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {validation.errors.map((err, idx) => (
                        <TableRow key={idx}>
                          <TableCell>{err.row}</TableCell>
                          <TableCell>{err.epicor_part_number}</TableCell>
                          <TableCell>
                            {err.errors.map((e, i) => (
                              <Typography key={i} variant="caption" display="block" color="error">
                                {e}
                              </Typography>
                            ))}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {validation.valid_rows > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {validation.valid_rows} valid rows will be imported. Rows with missing part numbers will be skipped.
                Images will be downloaded from the provided URLs.
              </Alert>
            )}
          </Box>
        )}

        {/* Step: Importing */}
        {step === 'importing' && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <Typography variant="h6">Importing...</Typography>
            <Typography variant="body2" color="text.secondary">
              Downloading images and saving product information. This may take a few minutes.
            </Typography>
          </Box>
        )}

        {/* Step: Done */}
        {step === 'done' && importResult?.result && (
          <Box>
            <Alert severity="success" sx={{ mb: 3 }}>
              {importResult.message}
            </Alert>

            <Box sx={{ display: 'flex', gap: 3, mb: 3 }}>
              <Paper sx={{ p: 2, flex: 1, textAlign: 'center', bgcolor: 'success.light', color: 'success.contrastText' }}>
                <Typography variant="h4">{importResult.result.successful}</Typography>
                <Typography variant="caption">Successful</Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1, textAlign: 'center', bgcolor: 'warning.light', color: 'warning.contrastText' }}>
                <Typography variant="h4">{importResult.result.partial}</Typography>
                <Typography variant="caption">Partial</Typography>
              </Paper>
              <Paper sx={{ p: 2, flex: 1, textAlign: 'center', bgcolor: importResult.result.failed > 0 ? 'error.light' : 'grey.100', color: importResult.result.failed > 0 ? 'error.contrastText' : 'text.primary' }}>
                <Typography variant="h4">{importResult.result.failed}</Typography>
                <Typography variant="caption">Failed</Typography>
              </Paper>
            </Box>

            {/* Per-row results */}
            <Typography variant="subtitle2" gutterBottom>Import Details:</Typography>
            <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 400 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Row</TableCell>
                    <TableCell>Part Number</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Images</TableCell>
                    <TableCell>Errors</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {importResult.result.rows.map((row, idx) => (
                    <TableRow key={idx}>
                      <TableCell>{row.row_number}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {row.epicor_part_number}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={row.status}
                          size="small"
                          color={getStatusColor(row.status)}
                        />
                      </TableCell>
                      <TableCell>{row.action}</TableCell>
                      <TableCell>
                        {row.images_downloaded > 0 && (
                          <Typography variant="caption" color="success.main">
                            {row.images_downloaded} ok
                          </Typography>
                        )}
                        {row.images_failed > 0 && (
                          <Typography variant="caption" color="error.main" sx={{ ml: 0.5 }}>
                            {row.images_failed} failed
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.errors.map((e, i) => (
                          <Typography key={i} variant="caption" display="block" color="error">
                            {e}
                          </Typography>
                        ))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        {step === 'upload' && (
          <Button onClick={handleClose}>Cancel</Button>
        )}
        {step === 'validation' && (
          <>
            <Button onClick={reset}>Back</Button>
            <Button
              variant="contained"
              onClick={handleImport}
              disabled={!validation || validation.valid_rows === 0}
              startIcon={<Upload />}
            >
              Import {validation?.valid_rows || 0} Rows
            </Button>
          </>
        )}
        {step === 'done' && (
          <Button variant="contained" onClick={handleClose}>
            Close
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

// ---------------------------------------------------------------------------
// Image Upload Section Component
// ---------------------------------------------------------------------------

interface ImageSectionProps {
  title: string;
  imageType: 'main' | 'color' | 'additional';
  images: ProductImage[];
  maxImages: number;
  productId: string;
  onImageUploaded: (image: ProductImage) => void;
  onImageDeleted: (imageId: string) => void;
  onImageMetaUpdated: (image: ProductImage) => void;
  showColorFields?: boolean;
  disabled?: boolean;
}

const ImageSection: React.FC<ImageSectionProps> = ({
  title,
  imageType,
  images,
  maxImages,
  productId,
  onImageUploaded,
  onImageDeleted,
  onImageMetaUpdated,
  showColorFields = false,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const canUpload = images.length < maxImages && !disabled;

  const handleFiles = async (files: FileList | null) => {
    if (!files || files.length === 0 || !canUpload) return;

    const remaining = maxImages - images.length;
    const toUpload = Array.from(files).slice(0, remaining);

    setUploading(true);
    try {
      for (const file of toUpload) {
        const uploaded = await productInfoService.uploadImage(
          productId,
          file,
          imageType,
        );
        onImageUploaded(uploaded);
      }
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDelete = async (imageId: string) => {
    setDeletingId(imageId);
    try {
      await productInfoService.deleteImage(productId, imageId);
      onImageDeleted(imageId);
    } catch (err) {
      console.error('Delete failed:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleColorNameChange = async (image: ProductImage, colorName: string) => {
    try {
      const updated = await productInfoService.updateImageMeta(productId, image.id, {
        color_name: colorName,
      });
      onImageMetaUpdated({ ...image, ...updated, file_url: image.file_url });
    } catch (err) {
      console.error('Update color name failed:', err);
    }
  };

  const handleColorHexChange = async (image: ProductImage, colorHex: string) => {
    try {
      const updated = await productInfoService.updateImageMeta(productId, image.id, {
        color_hex: colorHex,
      });
      onImageMetaUpdated({ ...image, ...updated, file_url: image.file_url });
    } catch (err) {
      console.error('Update color hex failed:', err);
    }
  };

  // Image specs per type
  const specs: Record<string, { width: number; height: number; ratio: string; maxMb: number; desc: string }> = {
    main: { width: 800, height: 800, ratio: '1:1 (square)', maxMb: 5, desc: 'Main product photos shown in detail gallery. Use white/clean background for best results.' },
    color: { width: 400, height: 400, ratio: '1:1 (square)', maxMb: 2, desc: 'Color variant swatches displayed as clickable circles in product detail.' },
    additional: { width: 800, height: 600, ratio: '4:3 (landscape)', maxMb: 5, desc: 'Feature, usage, or application images shown in the product gallery.' },
  };
  const spec = specs[imageType];

  return (
    <Box sx={{ mb: 3 }}>
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        {title} ({images.length}/{maxImages})
      </Typography>

      {/* Image requirements info */}
      <Box sx={{
        mb: 1.5, px: 2, py: 1, bgcolor: '#f0f7ff', borderRadius: 1, border: '1px solid', borderColor: '#cce0ff',
      }}>
        <Typography variant="caption" sx={{ color: '#1565c0', fontWeight: 600, display: 'block', mb: 0.3 }}>
          {spec.desc}
        </Typography>
        <Typography variant="caption" sx={{ color: '#1976d2' }}>
          Recommended: {spec.width} x {spec.height}px ({spec.ratio}) &bull; Max {spec.maxMb}MB &bull; Format: JPG, PNG, WebP
        </Typography>
      </Box>

      {/* Drop zone */}
      {canUpload && (
        <Box
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
          onClick={() => fileInputRef.current?.click()}
          sx={{
            border: '2px dashed',
            borderColor: dragOver ? 'primary.main' : 'grey.300',
            borderRadius: 2,
            p: 3,
            mb: 2,
            textAlign: 'center',
            cursor: 'pointer',
            bgcolor: dragOver ? 'action.hover' : '#fafafa',
            transition: 'all 0.2s',
            '&:hover': {
              borderColor: 'primary.main',
              bgcolor: 'action.hover',
            },
          }}
        >
          {uploading ? (
            <CircularProgress size={24} />
          ) : (
            <>
              <CloudUpload sx={{ fontSize: 36, color: 'grey.400', mb: 0.5 }} />
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.85rem' }}>
                {t('backoffice.productInfo.dropzone', 'Drag & drop images here or click to browse')}
              </Typography>
              <Typography variant="caption" color="text.disabled">
                {maxImages - images.length} more slot{maxImages - images.length !== 1 ? 's' : ''} available &bull; {spec.width}x{spec.height}px recommended
              </Typography>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            multiple
            hidden
            onChange={(e) => handleFiles(e.target.files)}
          />
        </Box>
      )}

      {/* Image grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(4, 1fr)',
          },
          gap: 2,
        }}
      >
        {images.map((img) => (
          <Box
            key={img.id}
            sx={{
              position: 'relative',
              border: '1px solid',
              borderColor: 'grey.300',
              borderRadius: 1,
              overflow: 'hidden',
            }}
          >
            <Box
              component="img"
              src={img.file_url}
              alt={img.alt_text || img.file_name}
              sx={{
                width: '100%',
                height: 140,
                objectFit: 'cover',
                display: 'block',
              }}
            />
            <IconButton
              size="small"
              color="error"
              disabled={deletingId === img.id}
              onClick={() => handleDelete(img.id)}
              sx={{
                position: 'absolute',
                top: 4,
                right: 4,
                bgcolor: 'rgba(255,255,255,0.85)',
                '&:hover': { bgcolor: 'rgba(255,255,255,1)' },
              }}
            >
              {deletingId === img.id ? <CircularProgress size={16} /> : <Delete fontSize="small" />}
            </IconButton>

            {showColorFields && (
              <Box sx={{ p: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
                <TextField
                  size="small"
                  label={t('backoffice.productInfo.colorName', 'Color Name')}
                  defaultValue={img.color_name || ''}
                  onBlur={(e) => handleColorNameChange(img, e.target.value)}
                  sx={{ flex: 1 }}
                />
                <input
                  type="color"
                  defaultValue={img.color_hex || '#ffffff'}
                  onChange={(e) => handleColorHexChange(img, e.target.value)}
                  style={{
                    width: 36,
                    height: 36,
                    border: 'none',
                    padding: 0,
                    cursor: 'pointer',
                    borderRadius: 4,
                  }}
                  title={t('backoffice.productInfo.colorHex', 'Pick color')}
                />
              </Box>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  );
};

// ---------------------------------------------------------------------------
// Main Page Component
// ---------------------------------------------------------------------------

const ProductInfoManagementPage: React.FC = () => {
  const { t } = useTranslation();

  // -- List state --
  const [products, setProducts] = useState<ProductInfoListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [infoFilter, setInfoFilter] = useState<InfoFilter>('all');
  const [brandFilter, setBrandFilter] = useState('');
  const [categoryItemFilter, setCategoryItemFilter] = useState('');
  const [familyFilter, setFamilyFilter] = useState('');
  const [subfamilyFilter, setSubfamilyFilter] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [filterOptions, setFilterOptions] = useState<{
    brands: { code: string; name: string; count: number }[];
    category_items: { code: string; name: string; count: number }[];
    families: { code: string; name: string; count: number }[];
    subfamilies: { code: string; name: string; count: number }[];
  }>({ brands: [], category_items: [], families: [], subfamilies: [] });

  // -- CSV import dialog state --
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);

  // -- Editor dialog state --
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorProductId, setEditorProductId] = useState<string | null>(null);
  const [editorProductName, setEditorProductName] = useState('');
  const [editorPartNumber, setEditorPartNumber] = useState('');
  const [editorTab, setEditorTab] = useState(0);
  const [editorLoading, setEditorLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // -- Editor form fields --
  const [descriptionId, setDescriptionId] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');
  const [finishType, setFinishType] = useState<string>('');
  const [mainImages, setMainImages] = useState<ProductImage[]>([]);
  const [colorImages, setColorImages] = useState<ProductImage[]>([]);
  const [additionalImages, setAdditionalImages] = useState<ProductImage[]>([]);
  const [hasExistingInfo, setHasExistingInfo] = useState(false);

  // -- Delete confirmation --
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // -- Snackbar --
  const [snack, setSnack] = useState<SnackState>({
    open: false,
    message: '',
    severity: 'success',
  });

  const showSnack = (message: string, severity: SnackState['severity'] = 'success') => {
    setSnack({ open: true, message, severity });
  };

  // -- Load products --
  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const hasInfo =
        infoFilter === 'with_info' ? true : infoFilter === 'without_info' ? false : undefined;

      const params = new URLSearchParams();
      params.append('page', String(page + 1));
      params.append('size', String(rowsPerPage));
      if (searchTerm.trim()) params.append('search', searchTerm.trim());
      if (hasInfo !== undefined) params.append('has_info', String(hasInfo));
      if (brandFilter) params.append('brand_code', brandFilter);
      if (categoryItemFilter) params.append('category_item_code', categoryItemFilter);
      if (familyFilter) params.append('family_code', familyFilter);
      if (subfamilyFilter) params.append('subfamily_code', subfamilyFilter);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);

      const res = await fetch(
        `${API_BASE_URL}/backoffice/product-info/?${params.toString()}`,
        { headers: getBackofficeAuthHeaders() as Record<string, string> },
      );
      if (!res.ok) throw new Error('Failed to load products');
      const data = await res.json();
      setProducts(data.items);
      setTotalCount(data.total_count);
    } catch (err) {
      console.error('Error loading products:', err);
      showSnack(
        err instanceof Error ? err.message : t('backoffice.productInfo.loadError', 'Failed to load products'),
        'error',
      );
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, searchTerm, infoFilter, brandFilter, categoryItemFilter, familyFilter, subfamilyFilter, sortBy, sortOrder, t]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  // Load filter options on mount
  useEffect(() => {
    fetch(`${API_BASE_URL}/backoffice/product-info/filter-options`, {
      headers: getBackofficeAuthHeaders() as Record<string, string>,
    })
      .then((r) => r.json())
      .then(setFilterOptions)
      .catch(() => {});
  }, []);

  // Debounced search
  const searchTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    if (searchTimerRef.current) clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(0);
    }, 500);
  };

  // -- Pagination --
  const handlePageChange = (_: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // -- Open editor --
  const openEditor = async (product: ProductInfoListItem) => {
    setEditorProductId(product.id);
    setEditorProductName(product.name);
    setEditorPartNumber(product.epicor_part_number);
    setEditorTab(0);
    setEditorOpen(true);
    setEditorLoading(true);

    // Reset form
    setDescriptionId('');
    setDescriptionEn('');
    setFinishType('');
    setMainImages([]);
    setColorImages([]);
    setAdditionalImages([]);
    setHasExistingInfo(false);

    try {
      const { additional_info } = await productInfoService.getProductInfo(product.id);
      if (additional_info) {
        setDescriptionId(additional_info.description_id || '');
        setDescriptionEn(additional_info.description_en || '');
        setFinishType(additional_info.finish_type || '');
        setMainImages(additional_info.main_images || []);
        setColorImages(additional_info.color_images || []);
        setAdditionalImages(additional_info.additional_images || []);
        setHasExistingInfo(true);
      }
    } catch (err) {
      console.error('Error loading product info:', err);
      showSnack(
        t('backoffice.productInfo.loadDetailError', 'Failed to load product details'),
        'error',
      );
    } finally {
      setEditorLoading(false);
    }
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditorProductId(null);
    // Refresh the list to pick up changes
    loadProducts();
  };

  // -- Save --
  const handleSave = async () => {
    if (!editorProductId) return;
    setSaving(true);
    try {
      await productInfoService.saveProductInfo(editorProductId, {
        description_id: descriptionId,
        description_en: descriptionEn,
        finish_type: finishType || undefined,
      });
      setHasExistingInfo(true);
      showSnack(t('backoffice.productInfo.saved', 'Product information saved successfully'));
    } catch (err) {
      console.error('Error saving product info:', err);
      showSnack(
        err instanceof Error ? err.message : t('backoffice.productInfo.saveError', 'Failed to save'),
        'error',
      );
    } finally {
      setSaving(false);
    }
  };

  // -- Delete all info --
  const handleDeleteAllInfo = async () => {
    if (!editorProductId) return;
    setDeleting(true);
    try {
      await productInfoService.deleteProductInfo(editorProductId);
      setDescriptionId('');
      setDescriptionEn('');
      setFinishType('');
      setMainImages([]);
      setColorImages([]);
      setAdditionalImages([]);
      setHasExistingInfo(false);
      setDeleteConfirmOpen(false);
      showSnack(t('backoffice.productInfo.deleted', 'All product info deleted'));
    } catch (err) {
      console.error('Error deleting product info:', err);
      showSnack(
        err instanceof Error ? err.message : t('backoffice.productInfo.deleteError', 'Failed to delete'),
        'error',
      );
    } finally {
      setDeleting(false);
    }
  };

  // -- Image callbacks --
  const handleImageUploaded = (type: 'main' | 'color' | 'additional') => (image: ProductImage) => {
    if (type === 'main') setMainImages((prev) => [...prev, image]);
    else if (type === 'color') setColorImages((prev) => [...prev, image]);
    else setAdditionalImages((prev) => [...prev, image]);
    // Mark that info now exists
    setHasExistingInfo(true);
  };

  const handleImageDeleted = (type: 'main' | 'color' | 'additional') => (imageId: string) => {
    if (type === 'main') setMainImages((prev) => prev.filter((i) => i.id !== imageId));
    else if (type === 'color') setColorImages((prev) => prev.filter((i) => i.id !== imageId));
    else setAdditionalImages((prev) => prev.filter((i) => i.id !== imageId));
  };

  const handleImageMetaUpdated = (type: 'main' | 'color' | 'additional') => (image: ProductImage) => {
    const updater = (prev: ProductImage[]) =>
      prev.map((i) => (i.id === image.id ? image : i));
    if (type === 'main') setMainImages(updater);
    else if (type === 'color') setColorImages(updater);
    else setAdditionalImages(updater);
  };

  // -- Filter change --
  const handleInfoFilterChange = (_: React.MouseEvent<HTMLElement>, value: InfoFilter | null) => {
    if (value !== null) {
      setInfoFilter(value);
      setPage(0);
    }
  };

  // -- Render --
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        {t('backoffice.productInfo.title', 'Product Information Management')}
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <Box sx={{ flexGrow: 1, minWidth: 300 }}>
            <TextField
              fullWidth
              label={t('backoffice.productInfo.search', 'Search')}
              variant="outlined"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t(
                'backoffice.productInfo.searchPlaceholder',
                'Search by name or part number...',
              )}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'action.active' }} />,
              }}
            />
          </Box>

          <ToggleButtonGroup
            value={infoFilter}
            exclusive
            onChange={handleInfoFilterChange}
            size="small"
          >
            <ToggleButton value="all">
              {t('backoffice.productInfo.filterAll', 'All')}
            </ToggleButton>
            <ToggleButton value="with_info">
              {t('backoffice.productInfo.filterWithInfo', 'With Info')}
            </ToggleButton>
            <ToggleButton value="without_info">
              {t('backoffice.productInfo.filterWithoutInfo', 'Without Info')}
            </ToggleButton>
          </ToggleButtonGroup>

          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadProducts}
            disabled={loading}
          >
            {t('backoffice.productInfo.refresh', 'Refresh')}
          </Button>
        </Box>

        {/* Second row: category filters + sort */}
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mt: 2 }}>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Brand</InputLabel>
            <Select
              value={brandFilter}
              label="Brand"
              onChange={(e) => { setBrandFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All Brands</MenuItem>
              {filterOptions.brands.map((b) => (
                <MenuItem key={b.code} value={b.code}>{b.name} ({b.count})</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={categoryItemFilter}
              label="Type"
              onChange={(e) => { setCategoryItemFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All Types</MenuItem>
              {filterOptions.category_items.map((c) => (
                <MenuItem key={c.code} value={c.code}>{c.name} ({c.count})</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Category</InputLabel>
            <Select
              value={familyFilter}
              label="Category"
              onChange={(e) => { setFamilyFilter(e.target.value); setSubfamilyFilter(''); setPage(0); }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {filterOptions.families.map((f) => (
                <MenuItem key={f.code} value={f.code}>{f.name} ({f.count})</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Sub-Category</InputLabel>
            <Select
              value={subfamilyFilter}
              label="Sub-Category"
              onChange={(e) => { setSubfamilyFilter(e.target.value); setPage(0); }}
            >
              <MenuItem value="">All Sub-Categories</MenuItem>
              {filterOptions.subfamilies.map((sf) => (
                <MenuItem key={sf.code} value={sf.code}>{sf.name} ({sf.count})</MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Sort By</InputLabel>
            <Select
              value={`${sortBy}-${sortOrder}`}
              label="Sort By"
              onChange={(e) => {
                const [field, order] = e.target.value.split('-');
                setSortBy(field);
                setSortOrder(order);
                setPage(0);
              }}
            >
              <MenuItem value="name-asc">Name A-Z</MenuItem>
              <MenuItem value="name-desc">Name Z-A</MenuItem>
              <MenuItem value="brand-asc">Brand A-Z</MenuItem>
              <MenuItem value="family-asc">Category A-Z</MenuItem>
              <MenuItem value="part_number-asc">Part # A-Z</MenuItem>
            </Select>
          </FormControl>

          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={async () => {
              try {
                await productInfoService.downloadTemplate();
              } catch {
                setSnack({ open: true, message: 'Failed to download template', severity: 'error' });
              }
            }}
          >
            Template
          </Button>

          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={async () => {
              try {
                await productInfoService.exportData();
                setSnack({ open: true, message: 'Export downloaded', severity: 'success' });
              } catch {
                setSnack({ open: true, message: 'Failed to export data', severity: 'error' });
              }
            }}
          >
            Export
          </Button>

          <Button
            variant="contained"
            color="primary"
            startIcon={<Upload />}
            onClick={() => setCsvDialogOpen(true)}
          >
            Import CSV
          </Button>
        </Box>
      </Paper>

      {/* CSV Import Dialog */}
      <CsvImportDialog
        open={csvDialogOpen}
        onClose={() => {
          setCsvDialogOpen(false);
          loadProducts();
        }}
      />

      {/* Product Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('backoffice.productInfo.colPartNumber', 'Part Number')}</TableCell>
                <TableCell>{t('backoffice.productInfo.colName', 'Name')}</TableCell>
                <TableCell>Brand</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Sub-Category</TableCell>
                <TableCell align="center">
                  {t('backoffice.productInfo.colImages', 'Images')}
                </TableCell>
                <TableCell align="center">
                  {t('backoffice.productInfo.colDescId', 'ID Desc')}
                </TableCell>
                <TableCell align="center">
                  {t('backoffice.productInfo.colDescEn', 'EN Desc')}
                </TableCell>
                <TableCell align="center">
                  {t('backoffice.productInfo.colActions', 'Actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : products.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      {t('backoffice.productInfo.noProducts', 'No products found')}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                products.map((product) => (
                  <TableRow
                    key={product.id}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => openEditor(product)}
                  >
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {product.epicor_part_number}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{product.name}</Typography>
                      {product.subfamily_name && (
                        <Typography variant="caption" color="text.secondary">
                          {product.subfamily_name}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {product.brand_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {product.category_item_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {product.family_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {product.subfamily_name || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell align="center">
                      {product.image_count > 0 ? (
                        <Chip
                          icon={<ImageIcon />}
                          label={product.image_count}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          0
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {product.has_description_id ? (
                        <Check color="success" fontSize="small" />
                      ) : (
                        <Remove color="disabled" fontSize="small" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      {product.has_description_en ? (
                        <Check color="success" fontSize="small" />
                      ) : (
                        <Remove color="disabled" fontSize="small" />
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={t('backoffice.productInfo.edit', 'Edit Info')}>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditor(product);
                          }}
                        >
                          <Edit />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleRowsPerPageChange}
        />
      </Paper>

      {/* ================================================================== */}
      {/* Editor Dialog (Full Screen)                                        */}
      {/* ================================================================== */}
      <Dialog open={editorOpen} onClose={closeEditor} fullScreen>
        {/* Header */}
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box>
            <Typography variant="h6" component="span">
              {editorProductName}
            </Typography>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              component="span"
              sx={{ ml: 2 }}
            >
              {editorPartNumber}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            {hasExistingInfo && (
              <Button
                color="error"
                variant="outlined"
                startIcon={<Delete />}
                onClick={() => setDeleteConfirmOpen(true)}
                disabled={editorLoading || saving}
              >
                {t('backoffice.productInfo.deleteAll', 'Delete All Info')}
              </Button>
            )}
            <Button
              variant="contained"
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
              onClick={handleSave}
              disabled={editorLoading || saving}
            >
              {t('backoffice.productInfo.save', 'Save')}
            </Button>
            <IconButton onClick={closeEditor}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {editorLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ p: 3, maxWidth: 1200, mx: 'auto' }}>
              <Tabs
                value={editorTab}
                onChange={(_, v) => setEditorTab(v)}
                sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}
              >
                <Tab
                  icon={<Description />}
                  iconPosition="start"
                  label={t('backoffice.productInfo.tabDescriptions', 'Descriptions')}
                />
                <Tab
                  icon={<ImageIcon />}
                  iconPosition="start"
                  label={t('backoffice.productInfo.tabImages', 'Images')}
                />
              </Tabs>

              {/* ====== Descriptions Tab ====== */}
              <TabPanel value={editorTab} index={0}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                  {/* Finish Type */}
                  <FormControl fullWidth>
                    <InputLabel>
                      {t('backoffice.productInfo.finishType', 'Finish Type')}
                    </InputLabel>
                    <Select
                      value={finishType}
                      label={t('backoffice.productInfo.finishType', 'Finish Type')}
                      onChange={(e) => setFinishType(e.target.value)}
                    >
                      <MenuItem value="">
                        <em>{t('backoffice.productInfo.none', 'None')}</em>
                      </MenuItem>
                      {FINISH_TYPES.map((ft) => (
                        <MenuItem key={ft} value={ft}>
                          {ft}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* Description ID - Rich Text */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {t(
                        'backoffice.productInfo.descriptionId',
                        'Description (Bahasa Indonesia)',
                      )}
                    </Typography>
                    <Box
                      sx={{
                        '.ql-container': { minHeight: 200, fontSize: '14px' },
                        '.ql-editor': { minHeight: 200 },
                        '.ql-toolbar': { borderRadius: '4px 4px 0 0', bgcolor: '#fafafa' },
                        '.ql-container.ql-snow': { borderRadius: '0 0 4px 4px' },
                        mb: 2,
                      }}
                    >
                      <ReactQuill
                        theme="snow"
                        value={descriptionId}
                        onChange={setDescriptionId}
                        modules={QUILL_MODULES}
                        formats={QUILL_FORMATS}
                        placeholder={t(
                          'backoffice.productInfo.descriptionIdPlaceholder',
                          'Enter product description in Bahasa Indonesia...',
                        )}
                      />
                    </Box>
                  </Box>

                  {/* Description EN - Rich Text */}
                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      {t('backoffice.productInfo.descriptionEn', 'Description (English)')}
                    </Typography>
                    <Box
                      sx={{
                        '.ql-container': { minHeight: 200, fontSize: '14px' },
                        '.ql-editor': { minHeight: 200 },
                        '.ql-toolbar': { borderRadius: '4px 4px 0 0', bgcolor: '#fafafa' },
                        '.ql-container.ql-snow': { borderRadius: '0 0 4px 4px' },
                      }}
                    >
                      <ReactQuill
                        theme="snow"
                        value={descriptionEn}
                        onChange={setDescriptionEn}
                        modules={QUILL_MODULES}
                        formats={QUILL_FORMATS}
                        placeholder={t(
                          'backoffice.productInfo.descriptionEnPlaceholder',
                          'Enter product description in English...',
                        )}
                      />
                    </Box>
                  </Box>
                </Box>
              </TabPanel>

              {/* ====== Images Tab ====== */}
              <TabPanel value={editorTab} index={1}>
                {!hasExistingInfo && mainImages.length === 0 && colorImages.length === 0 && additionalImages.length === 0 ? (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {t(
                      'backoffice.productInfo.saveFirst',
                      'Save the description/finish type first before uploading images, or drop an image to auto-create the record.',
                    )}
                  </Alert>
                ) : null}

                {/* Main Images */}
                {editorProductId && (
                  <>
                    <ImageSection
                      title={t('backoffice.productInfo.mainImages', 'Main Images')}
                      imageType="main"
                      images={mainImages}
                      maxImages={3}
                      productId={editorProductId}
                      onImageUploaded={handleImageUploaded('main')}
                      onImageDeleted={handleImageDeleted('main')}
                      onImageMetaUpdated={handleImageMetaUpdated('main')}
                    />

                    {/* Color Images */}
                    <ImageSection
                      title={t('backoffice.productInfo.colorImages', 'Color Images')}
                      imageType="color"
                      images={colorImages}
                      maxImages={5}
                      productId={editorProductId}
                      onImageUploaded={handleImageUploaded('color')}
                      onImageDeleted={handleImageDeleted('color')}
                      onImageMetaUpdated={handleImageMetaUpdated('color')}
                      showColorFields
                    />

                    {/* Additional Images */}
                    <ImageSection
                      title={t('backoffice.productInfo.additionalImages', 'Additional Images')}
                      imageType="additional"
                      images={additionalImages}
                      maxImages={5}
                      productId={editorProductId}
                      onImageUploaded={handleImageUploaded('additional')}
                      onImageDeleted={handleImageDeleted('additional')}
                      onImageMetaUpdated={handleImageMetaUpdated('additional')}
                    />
                  </>
                )}
              </TabPanel>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* ================================================================== */}
      {/* Delete Confirmation Dialog                                         */}
      {/* ================================================================== */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>
          {t('backoffice.productInfo.deleteConfirmTitle', 'Delete All Product Info?')}
        </DialogTitle>
        <DialogContent>
          <Typography>
            {t(
              'backoffice.productInfo.deleteConfirmMessage',
              'This will permanently delete all descriptions, images, and additional information for this product. This action cannot be undone.',
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={deleting}>
            {t('backoffice.productInfo.cancel', 'Cancel')}
          </Button>
          <Button
            color="error"
            variant="contained"
            onClick={handleDeleteAllInfo}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <Delete />}
          >
            {t('backoffice.productInfo.confirmDelete', 'Delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ================================================================== */}
      {/* Snackbar                                                           */}
      {/* ================================================================== */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          severity={snack.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ProductInfoManagementPage;
